import { useEffect, useState } from 'react'

import { useDebounce } from '@uidotdev/usehooks'
import { FormProvider, useFormContext, useWatch } from 'react-hook-form'
import { usePrevious } from 'react-use'
import { formatUnits } from 'viem'
import { base, mainnet } from 'viem/chains'

import { SplitsLink as Link } from '~/components/Link'
import { EarnDepositSection } from '~/components/forms/earn/EarnDepositSection'
import { EarnWithdrawSection } from '~/components/forms/earn/EarnWithdrawSection'
import { EarnFormValues, earnSchema } from '~/components/forms/earn/earnSchema'
import { useAddEarnToBatch } from '~/components/forms/earn/hooks/useAddEarnToBatch'
import {
  useCreateEarnTransactionSubmit,
  useEarnForm,
  useEarnLoggingData,
  useEarnRequest,
} from '~/components/forms/earn/hooks/useEarnForm'
import { SenderSection } from '~/components/forms/shared/fields/SenderSection'
import {
  REFRESH_QUOTE_INTERVAL,
  useGetSwapLoggingData,
  useSwapQuoteQuery,
  useSwapRequest,
} from '~/components/forms/swap/hooks/useSwap'
import { TransactionDialogContent } from '~/components/smart-accounts/transactions/TransactionDialogContent'
import { createTransactionHandlers } from '~/components/smart-accounts/transactions/createTransactionHandlers'
import { SupportedChainId } from '~/constants/chains'
import { EARN_GUIDE_URL } from '~/constants/externalUrls'
import { useActiveOrgId, useRequireMemo } from '~/hooks/org'
import { useApproveAndSubmitTransaction } from '~/hooks/smart-account-actions/useApproveAndSubmitTransaction'
import { buildAddToBatchDropdownOptions } from '~/hooks/useAddCurrentFormToBatch'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { EarnAction, IAddress } from '~/types'
import { Address, EvmAddress } from '~/utils/address'
import { getSettlement } from '~/utils/swap'
import {
  BASE_USDC,
  MAINNET_USDC,
  getVaultProviderName,
  MORPHO_FEE_WRAPPER_BASE_USDC,
  MORPHO_FEE_WRAPPER_MAINNET_USDC,
} from '~/utils/vault'

export const EarnDepositDialog = ({
  defaultAccount,
  initialAccount,
  defaultAmount,
  defaultMemo,
  chainId,
  setIsOpen,
}: {
  /** Locks the sender to this account and hides the picker */
  defaultAccount?: IAddress
  /** Preselects this account but keeps the picker */
  initialAccount?: IAddress
  defaultAmount?: string
  defaultMemo?: string
  chainId: typeof base.id | typeof mainnet.id
  setIsOpen: (open: boolean) => void
}) => {
  return (
    <EarnFormDialogContent
      defaultAccount={defaultAccount}
      initialAccount={initialAccount}
      defaultAmount={defaultAmount}
      defaultMemo={defaultMemo}
      chainId={chainId}
      assetToken={chainId === mainnet.id ? MAINNET_USDC : BASE_USDC}
      vaultToken={
        chainId === mainnet.id
          ? MORPHO_FEE_WRAPPER_MAINNET_USDC
          : MORPHO_FEE_WRAPPER_BASE_USDC
      }
      action={EarnAction.DEPOSIT}
      setIsOpen={setIsOpen}
    />
  )
}

export const EarnFormDialogContent = ({
  defaultAccount,
  initialAccount,
  defaultAmount,
  defaultMemo,
  assetToken: defaultAssetToken,
  vaultToken: defaultVaultToken,
  chainId,
  action,
  setIsOpen,
}: {
  assetToken: IAddress
  vaultToken: IAddress
  chainId: SupportedChainId
  action: EarnAction
  defaultAccount?: IAddress
  initialAccount?: IAddress
  defaultAmount?: string
  defaultMemo?: string
  setIsOpen: (open: boolean) => void
}) => {
  const methods = useEarnForm({
    // Only defaultAccount is passed further down, so the picker stays for initialAccount.
    defaultAccount: defaultAccount ?? initialAccount,
    defaultAmount,
    defaultMemo,
    assetToken: defaultAssetToken,
    vaultToken: defaultVaultToken,
    chainId,
    action,
  })

  return (
    <FormProvider {...methods}>
      <EarnFormDialog
        defaultAccount={defaultAccount}
        chainId={chainId}
        action={action}
        setIsOpen={setIsOpen}
      />
    </FormProvider>
  )
}

const EarnFormDialog = ({
  chainId,
  action,
  defaultAccount,
  setIsOpen,
}: {
  chainId: SupportedChainId
  action: EarnAction
  defaultAccount?: IAddress
  setIsOpen: (open: boolean) => void
}) => {
  const methods = useFormContext<EarnFormValues>()

  const { control } = methods

  const account = useWatch({ control, name: 'account' })

  const vaultToken = useWatch({ control, name: 'vault.token' })
  const shares = useWatch({ control, name: 'vault.amount' })
  const vaultChainId = useWatch({ control, name: 'vault.chainId' })

  const assetToken = useWatch({ control, name: 'asset.token' })
  const assets = useWatch({ control, name: 'asset.amount' })
  const assetChainId = useWatch({ control, name: 'asset.chainId' })

  const sellToken = useWatch({ control, name: 'swapData.sellToken.token' })
  const sellAmount = useWatch({ control, name: 'swapData.sellToken.amount' })
  const sellChainId = useWatch({ control, name: 'swapData.sellToken.chainId' })

  const { smartAccounts } = useSmartAccounts()
  const senderAccount = smartAccounts.find((a) => a.address === account)

  const assetTokenMetadata = useTokenMetadata({
    address: assetToken,
    chainId,
  })
  const vaultTokenMetadata = useTokenMetadata({
    address: vaultToken,
    chainId,
  })
  const sellTokenMetadata = useTokenMetadata({ address: sellToken, chainId })

  const { getLoggingData } = useGetSwapLoggingData(
    useEarnLoggingData({
      account,
      sellToken: sellToken ?? assetToken,
      sellAmount: sellAmount ?? '0',
      sellChainId: sellChainId ?? assetChainId,
      vaultToken,
      vaultChainId,
      vaultAmount: shares,
      assetToken,
      assetAmount: assets,
      assetChainId: assetChainId,
      action,
    }),
  )

  const { isLoading, dataUpdatedAt } = useEarnSwapQuote({
    chainId,
    action,
  })

  const memo = useWatch({ control, name: 'memo' })
  const requireMemo = useRequireMemo()

  const [insufficientBalance, setInsufficientBalance] = useState(false)

  const isValidForm =
    Object.keys(methods.formState.errors ?? {}).length === 0 &&
    earnSchema.safeParse(methods.getValues()).success &&
    !isLoading &&
    !insufficientBalance &&
    !(requireMemo && !memo?.trim())

  const {
    approveAndSubmitTransaction,
    isProcessing: isProcessingApproveAndSubmit,
  } = useApproveAndSubmitTransaction()

  const activeOrgId = useActiveOrgId()

  const { createTransactionOnSubmit, isProcessingTransaction } =
    useCreateEarnTransactionSubmit({
      activeOrgId,
      setIsOpen,
      assetToken: assetTokenMetadata,
      vaultToken: vaultTokenMetadata,
      sellToken: sellTokenMetadata,
    })

  const isProcessing = isProcessingTransaction || isProcessingApproveAndSubmit

  const transactionHandlers = createTransactionHandlers<EarnFormValues>({
    methods,
    senderAccount: senderAccount!,
    getLoggingData: (data) =>
      getLoggingData({
        routerType: data.swapData?.swapRouter.toString(),
      }),
    createTransactionOnSubmit,
    approveAndSubmitTransaction,
  })

  const shouldHideIntermediateToken =
    action === EarnAction.DEPOSIT && sellToken !== assetToken
  const earnProvider = getVaultProviderName(vaultToken)

  const batchTitle =
    action === EarnAction.DEPOSIT
      ? `Earn${
          senderAccount && defaultAccount ? ` from ${senderAccount.name}` : ''
        }`
      : 'Remove from position'

  const {
    handleAddToBatch,
    isProcessing: isAddingToBatch,
    isMetadataReady: isBatchMetadataReady,
    transactionTypes,
    canAddToBatch,
  } = useAddEarnToBatch({
    senderAccount,
    title: batchTitle,
    action,
    chainId,
    setIsOpen,
  })

  return (
    <TransactionDialogContent
      id="earnDetails"
      title={batchTitle}
      isValidForm={isValidForm}
      isLoading={isProcessing}
      handlers={transactionHandlers}
      smartAccountAddress={account}
      addressesToWatch={[account]}
      chainId={chainId}
      eventType="Earn"
      transactionTypes={transactionTypes}
      // We want to hide the intermediate token which ends up in the account after the swap in a small amount.
      stateChangesToHide={
        shouldHideIntermediateToken
          ? {
              [account]: { [EvmAddress.format(assetToken)]: true },
            }
          : undefined
      }
      refreshData={
        dataUpdatedAt
          ? {
              dataUpdatedAt: dataUpdatedAt,
              refetchInterval: REFRESH_QUOTE_INTERVAL,
              isLoading: isLoading,
            }
          : undefined
      }
      reviewDropdownOptions={buildAddToBatchDropdownOptions({
        canAddToBatch,
        isDisabled: !isValidForm || !isBatchMetadataReady || isAddingToBatch,
        onClick: handleAddToBatch,
      })}
    >
      {action === EarnAction.DEPOSIT && (
        <p>
          Deposit {assetTokenMetadata?.symbol ?? ''} into a yield-bearing vault
          without losing access to your funds. Powered by {earnProvider}.{' '}
          <Link path={EARN_GUIDE_URL} target="tab" underline>
            Learn more
          </Link>
        </p>
      )}
      <EarnFormSection
        defaultAccount={defaultAccount}
        chainId={chainId}
        action={action}
        onInsufficientBalanceChange={setInsufficientBalance}
      />
    </TransactionDialogContent>
  )
}

const EarnFormSection = ({
  defaultAccount,
  chainId,
  action,
  onInsufficientBalanceChange,
}: {
  defaultAccount?: IAddress
  chainId: SupportedChainId
  action: EarnAction
  onInsufficientBalanceChange: (insufficient: boolean) => void
}) => {
  const methods = useFormContext<EarnFormValues>()

  const { control, setValue, reset } = methods

  const account = useWatch({ control, name: 'account' })

  const previousAccount = usePrevious(account)

  useEarnSwapQuote({
    chainId,
    action,
  })

  return (
    <div className="space-y-4">
      {!defaultAccount && (
        <SenderSection
          control={control}
          senderInputName="account"
          title="From"
          onSelect={(value) => {
            if (previousAccount !== undefined) {
              reset(undefined, { keepDefaultValues: true })
            }
            setValue('account', value)
          }}
          hideBalance={true}
        />
      )}
      <div className="relative space-y-2">
        {action === EarnAction.DEPOSIT ? (
          <EarnDepositSection
            onInsufficientBalanceChange={onInsufficientBalanceChange}
          />
        ) : (
          <EarnWithdrawSection
            onInsufficientBalanceChange={onInsufficientBalanceChange}
          />
        )}
      </div>
    </div>
  )
}

const useEarnSwapQuote = ({
  chainId,
  action,
}: {
  chainId: SupportedChainId
  action: EarnAction
}) => {
  const methods = useFormContext<EarnFormValues>()

  const { control, setValue } = methods

  const account = useWatch({ control, name: 'account' })

  const vaultToken = useWatch({ control, name: 'vault.token' })
  const shares = useWatch({ control, name: 'vault.amount' })
  const vaultChainId = useWatch({ control, name: 'vault.chainId' })

  const assetToken = useWatch({ control, name: 'asset.token' })
  const assets = useWatch({ control, name: 'asset.amount' })
  const assetChainId = useWatch({ control, name: 'asset.chainId' })

  const sellToken = useWatch({ control, name: 'swapData.sellToken.token' })
  const sellAmount = useWatch({ control, name: 'swapData.sellToken.amount' })
  const sellChainId = useWatch({ control, name: 'swapData.sellToken.chainId' })

  const assetTokenMetadata = useTokenMetadata({
    address: assetToken,
    chainId,
  })
  const vaultTokenMetadata = useTokenMetadata({
    address: vaultToken,
    chainId,
  })

  const swapRequest = useSwapRequest(
    useEarnRequest({
      account,
      sellToken: sellToken ?? assetToken,
      sellAmount: sellAmount ?? '0',
      sellChainId: sellChainId ?? assetChainId,
      vaultToken,
      vaultChainId,
      vaultAmount: shares,
      assetToken,
      assetAmount: assets,
      assetChainId: assetChainId,
      action,
    }),
  )

  const debouncedSwapRequest = useDebounce(swapRequest, 250)

  const {
    data: swapQuote,
    isFetching,
    dataUpdatedAt,
    refetch,
  } = useSwapQuoteQuery(debouncedSwapRequest)

  useEffect(() => {
    if (!swapQuote) {
      setValue('calls', [])
      return
    }

    if (swapQuote?.success && action === EarnAction.DEPOSIT) {
      setValue(
        'vault.amount',
        formatUnits(
          swapQuote.data.buyAmount,
          vaultTokenMetadata?.decimals ?? 18,
        ),
      )

      // If the sell token is the same as the asset token, we can just use the sell amount
      // Otherwise, we need to use the first step's buy amount, as the sell amount is the amount after the first step
      if (Address.equal(swapQuote.data.sellToken, assetToken)) {
        setValue(
          'asset.amount',
          formatUnits(
            swapQuote.data.sellAmount,
            assetTokenMetadata?.decimals ?? 18,
          ),
        )
      } else {
        setValue(
          'asset.amount',
          formatUnits(
            swapQuote.data.steps?.[0]?.buyAmount ?? 0n,
            assetTokenMetadata?.decimals ?? 18,
          ),
        )
      }
      setValue(
        'swapData.providerTransactionId',
        getSettlement(swapQuote.data)?.providerTransactionId,
      )
      setValue(
        'swapData.swapRouter',
        swapQuote.data.steps?.[0]?.routerType ?? swapQuote.data.routerType,
      )
    } else if (swapQuote?.success && action === EarnAction.WITHDRAW) {
      setValue(
        'vault.amount',
        formatUnits(
          swapQuote.data.sellAmount,
          vaultTokenMetadata?.decimals ?? 18,
        ),
      )
    }
    setValue(
      'calls',
      swapQuote.data.calls.map((call) => ({
        ...call,
        value: BigInt(call.value),
      })),
    )
    // useTokenMetadata returns a new object each render; depend on decimals, not the object, so this effect doesn't re-fire every render.
  }, [
    swapQuote,
    action,
    chainId,
    vaultTokenMetadata?.decimals,
    assetTokenMetadata?.decimals,
    setValue,
    assetToken,
  ])

  return {
    isLoading: isFetching,
    dataUpdatedAt,
    refetch,
  }
}
